import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as Styles from './sign-up-requirements-modal.module.scss';
import AvatarSelect from './avatar-select';
import { avatarList } from './avatar';
import BirthdayField from './birthday-field';
import { ADULT_AGE, COPPA_AGE, canUserJoinClass } from '../utils/user';
import RoleField from './role-field';
import GradeBandField from './grade-band-field';
import Button from './button';
import FormProcessing from './form-processing';
import FormError from './form-error';
import { useAuth } from '../contexts/AuthContext';
import { useSite } from '../contexts/SiteContext';
import ClassCodeField from './class-code-field';
import { getClassCode, removeClassCode } from '../utils/class-code';

function SignUpRequirementsModal({ show }) {
  const dialog = useRef(null);
  const [error, setError] = useState('');
  const [avatar, setAvatar] = useState(avatarList[0]);
  const [birthday, setBirthday] = useState('');
  const [classCode, setClassCode] = useState('');
  const [age, setAge] = useState(0);
  const [roles, setRoles] = useState([]);
  const [gradeBand, setGradeBand] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { signUpDemographics, setAvatar: setProfileAvatar } = useAuth();
  const { joinClassroom } = useSite();
  const [animate, setAnimate] = useState('');

  useEffect(() => {
    if (dialog.current && show) {
      setAnimate('enter');
      dialog.current.showModal();
    } else if (dialog.current && !show) {
      setAnimate('exit');
    }
  }, [dialog, show]);

  async function handleSubmit(e) {
    e.preventDefault();

    if (isProcessing) {
      return;
    }

    if (avatar === avatarList[0]) {
      setError('Please select an avatar.');
      dialog.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (roles.length === 0) {
      setError('Please select a role.');
      dialog.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (!gradeBand && age >= COPPA_AGE) {
      setError('Please select a grade.');
      dialog.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsProcessing(true);

      try {
        await signUpDemographics(null, null, gradeBand, roles, age, new Date(birthday));
        await setProfileAvatar(avatar);

        if (window.ga) {
          window.ga('set', 'dimension57', roles[0]);
        }

        if (classCode && canUserJoinClass({ roles })) {
          try {
            await joinClassroom(classCode);
          } catch (classError) {
            // Intentionally skip error
          }
        }
        removeClassCode();
      } catch (catchError) {
        // Intentionally skip error
      }

      setIsProcessing(false);
    }
  }

  function handleDialogAnimateEnd() {
    if (animate === 'exit') {
      dialog.current.close();
    }
  }

  function handleDialogKeyPress(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
    }
  }

  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <dialog
        ref={dialog}
        onKeyDown={handleDialogKeyPress}
        className={Styles.dialog}
        onAnimationEnd={handleDialogAnimateEnd}
        data-animate={animate}
      >
        <div className={Styles.title}>Just a few more things...</div>
        <FormError message={error} />
        <form onSubmit={handleSubmit}>
          <div className={Styles.avatarHelper}>
            <strong>Choose your favorite avatar*</strong>
            <br />
            (You can change it from your profile at any time)
          </div>
          <AvatarSelect changeAvatarHandler={(a) => setAvatar(a)} avatar={avatar} className={Styles.avatar} />
          <div className={classNames('form-group')}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={classNames('form-label')}>
              Birthday*
              <BirthdayField
                setAge={(a) => {
                  setAge(a);
                  setRoles(a >= ADULT_AGE ? [] : ['student']);
                }}
                birthday={birthday}
                setBirthday={setBirthday}
              />
            </label>
          </div>
          {age >= ADULT_AGE && (
            <div className={classNames('form-group')}>
              <div className={classNames('form-label')}>What best describes you?*</div>
              <RoleField role={roles[0] ? roles[0] : null} setRole={(role) => setRoles([role])} />
            </div>
          )}
          {age >= COPPA_AGE && (
            <div className={classNames('form-group')}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className={classNames('form-label')}>
                Grade*
                <GradeBandField setGradeBand={(gb) => setGradeBand(gb)} />
              </label>
              <div
                className="form-helper"
                style={{ opacity: roles.indexOf('teacher') >= 0 || roles.indexOf('parent') >= 0 ? 1 : 0 }}
              >
                Grownups, select the grade band that best describes your student(s).
              </div>
            </div>
          )}
          {((age >= ADULT_AGE && canUserJoinClass({ roles })) || (age <= ADULT_AGE && age > 0)) && (
            <div className={classNames('form-group')}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className={classNames('form-label')}>
                Class Code <span style={{ fontWeight: 'normal' }}>(If you have one)</span>
              </label>
              <ClassCodeField
                defaultValue={getClassCode()}
                onValidCode={(code) => setClassCode(code)}
                onInvalidCode={() => setClassCode('')}
              />
            </div>
          )}
          {age > 0 && (
            <div className={classNames('form-group')}>
              <FormProcessing isProcessing={isProcessing}>
                <Button size="md" type="submit" theme="blue" width="md">
                  Finish Sign Up
                </Button>
              </FormProcessing>
            </div>
          )}
        </form>
      </dialog>
    </div>
  );
}

SignUpRequirementsModal.propTypes = {
  show: PropTypes.bool.isRequired,
};

export default SignUpRequirementsModal;
